import { isHttpClientError } from 'core/infra/http/HttpClient'

import { createAgnosticHttpClient } from 'auth/infra/createAgnosticHttpClient'

import { mapHttpClientErrorToAddEmployeeResponseError } from '../infra/CarrierMapper'
import {
  type AddEmployeeResponseError,
  CarrierRepository,
  type AddEmployeeParams,
} from '../infra/CarrierRepository'
import { ShipmentRepository } from '../infra/ShipmentRepository'

export type CreateAndAssignDriverDependencies = {
  carrierRepository: CarrierRepository
  shipmentRepository: ShipmentRepository
}

export type CreateAndAssignDriverParams = AddEmployeeParams & {
  shipmentUUID: string
}

export class CreateAndAssignDriverError extends Error {
  errors?: AddEmployeeResponseError

  constructor(message?: string, options?: ErrorOptions, errors?: AddEmployeeResponseError) {
    super(message, options)
    this.name = 'CreateAndAssignDriverError'
    this.errors = errors
  }
}

export function createAndAssignDriverFactory({
  carrierRepository,
  shipmentRepository,
}: CreateAndAssignDriverDependencies) {
  return async function addEmployee({
    carrierUUID,
    shipmentUUID,
    employee,
    config,
  }: CreateAndAssignDriverParams) {
    let driver = undefined
    try {
      driver = await carrierRepository.addEmployee({
        carrierUUID,
        employee,
        config,
      })
    } catch (error) {
      const message = 'Failed to create new driver'
      if (isHttpClientError(error)) {
        const errors = mapHttpClientErrorToAddEmployeeResponseError(error)
        throw new CreateAndAssignDriverError(message, { cause: error }, errors)
      }
      throw new Error(message, { cause: error as Error })
    }

    try {
      await shipmentRepository.assignDriverToShipment({
        driverUUID: driver.uuid,
        shipmentUUID,
        config,
      })
    } catch (error) {
      throw new Error('Failed to assign the driver', { cause: error as Error })
    }

    return driver
  }
}

export function createAndAssignDriver({
  carrierUUID,
  shipmentUUID,
  employee,
  config,
}: CreateAndAssignDriverParams) {
  const httpClient = createAgnosticHttpClient()
  const carrierRepository = new CarrierRepository({ httpClient })
  const shipmentRepository = new ShipmentRepository({ httpClient })
  const addEmployee = createAndAssignDriverFactory({ carrierRepository, shipmentRepository })

  return addEmployee({ carrierUUID, shipmentUUID, employee, config })
}
